/**
 * InstallPWABanner — Contextual "Add to Home Screen" prompt.
 *
 * Apple-Pro editorial style: glass dark surface, subtle entrance,
 * single dominant CTA, dismissable. Detects:
 *   • Already-installed (standalone)  → no-op
 *   • Android/Chrome with `beforeinstallprompt`  → native install button
 *   • iOS Safari  → visual "Share ⎙ → Añadir a inicio" instructions
 *   • Desktop / unsupported  → no-op
 *
 * Dismissal is persisted in localStorage with a 14-day TTL so we don't
 * nag the user. The banner only renders after a short delay (~1.5s) to
 * avoid stealing attention from first-paint content.
 */
import { useEffect, useState } from "react";
import { Share, Plus, X, Download } from "lucide-react";

const DISMISS_KEY = "pyg_pwa_install_dismissed_at";
const DISMISS_TTL_DAYS = 14;

function isStandalone() {
  try {
    if (window.matchMedia("(display-mode: standalone)").matches) return true;
    if (window.navigator.standalone === true) return true;
  } catch {
    /* noop */
  }
  return false;
}

function detectPlatform() {
  const ua = (navigator.userAgent || "").toLowerCase();
  const isIOS = /iphone|ipad|ipod/.test(ua) || (ua.includes("mac") && "ontouchend" in document);
  const isAndroid = /android/.test(ua);
  const isSafari =
    /safari/.test(ua) && !/crios|fxios|edgios|opios/.test(ua);
  return { isIOS, isAndroid, isSafari };
}

function isDismissedRecently() {
  try {
    const ts = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
    if (!ts) return false;
    const ageMs = Date.now() - ts;
    return ageMs < DISMISS_TTL_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
}

export default function InstallPWABanner() {
  const [visible, setVisible] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [{ isIOS, isAndroid, isSafari }] = useState(detectPlatform);

  // Capture Android's native install prompt early.
  useEffect(() => {
    const onBefore = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", onBefore);
    const onInstalled = () => setVisible(false);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBefore);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  // Decide visibility after a short delay (avoid stealing the hero).
  useEffect(() => {
    if (isStandalone()) return;
    if (isDismissedRecently()) return;

    // Show on iOS Safari (no programmatic install) OR on Android once
    // beforeinstallprompt has fired. Desktop / other → no-op.
    const showCandidate = (isIOS && isSafari) || isAndroid;
    if (!showCandidate) return;

    const t = setTimeout(() => {
      // Android needs the deferred prompt to be useful, but we still
      // show a hint even if it never fires (some browsers withhold it
      // after repeated visits — user can still install via menu).
      setVisible(true);
    }, 1500);
    return () => clearTimeout(t);
  }, [isIOS, isAndroid, isSafari, deferredPrompt]);

  if (!visible) return null;

  const handleDismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      /* noop */
    }
    setVisible(false);
  };

  const handleAndroidInstall = async () => {
    if (!deferredPrompt) return;
    try {
      deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      if (choice?.outcome === "accepted") {
        setVisible(false);
      } else {
        handleDismiss();
      }
      setDeferredPrompt(null);
    } catch {
      handleDismiss();
    }
  };

  return (
    <div
      data-testid="install-pwa-banner"
      role="dialog"
      aria-label="Instalar Play Your Game"
      className="fixed left-1/2 -translate-x-1/2 z-50 w-[calc(100%-32px)] max-w-[420px] bottom-4 sm:bottom-6 animate-pwa-rise"
      style={{ animation: "pwaRise 480ms cubic-bezier(0.16,1,0.3,1) both" }}
    >
      <div
        className="relative rounded-2xl border border-white/10 bg-[#050505]/95 backdrop-blur-xl shadow-[0_20px_60px_-20px_rgba(0,0,0,0.7)] overflow-hidden"
      >
        {/* Top hairline accent */}
        <div
          aria-hidden
          className="absolute top-0 left-0 right-0 h-px"
          style={{
            background:
              "linear-gradient(to right, transparent, rgba(41,188,155,0.45), transparent)",
          }}
        />

        <button
          onClick={handleDismiss}
          data-testid="install-pwa-dismiss"
          aria-label="Cerrar"
          className="absolute top-3 right-3 w-7 h-7 rounded-full flex items-center justify-center text-white/40 hover:text-white/80 hover:bg-white/[0.06] transition-colors"
        >
          <X size={14} />
        </button>

        <div className="p-5 sm:p-6">
          <div className="flex items-start gap-4">
            <div
              aria-hidden
              className="w-11 h-11 rounded-xl bg-[#29BC9B]/15 border border-[#29BC9B]/30 flex items-center justify-center shrink-0"
            >
              <Download size={18} className="text-[#29BC9B]" />
            </div>

            <div className="flex-1 min-w-0 pr-6">
              <div className="text-[10px] tracking-[0.28em] uppercase text-[#29BC9B] font-semibold mb-1.5">
                Instalar app
              </div>
              <h3 className="text-white text-[15px] sm:text-base font-semibold leading-snug tracking-tight">
                Lleva tu evolución a tu pantalla de inicio
              </h3>
              <p className="text-[12.5px] text-white/55 leading-relaxed mt-1.5">
                {isIOS && isSafari
                  ? "Acceso directo en un toque, sin barra del navegador."
                  : "Instala Play Your Game como app y abre en un toque."}
              </p>
            </div>
          </div>

          {/* iOS — visual instructions */}
          {isIOS && isSafari && (
            <ol
              data-testid="install-pwa-ios-steps"
              className="mt-5 space-y-3 text-[12.5px] text-white/75"
            >
              <li className="flex items-center gap-3">
                <span className="text-[10px] tracking-[0.22em] uppercase text-white/35 font-semibold tabular-nums w-5">
                  01
                </span>
                <span className="flex-1">
                  Toca{" "}
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-white/[0.06] border border-white/10 align-middle mx-0.5">
                    <Share size={13} className="text-[#29BC9B]" />
                  </span>{" "}
                  en la barra inferior de Safari.
                </span>
              </li>
              <li className="flex items-center gap-3">
                <span className="text-[10px] tracking-[0.22em] uppercase text-white/35 font-semibold tabular-nums w-5">
                  02
                </span>
                <span className="flex-1">
                  Elige{" "}
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-white/[0.06] border border-white/10 align-middle text-white/90 font-medium">
                    Añadir a inicio
                    <Plus size={11} className="text-[#29BC9B]" />
                  </span>
                </span>
              </li>
            </ol>
          )}

          {/* Android — native prompt */}
          {isAndroid && (
            <div className="mt-5 flex items-center gap-3">
              {deferredPrompt ? (
                <button
                  onClick={handleAndroidInstall}
                  data-testid="install-pwa-android-install"
                  className="group flex-1 inline-flex items-center justify-center gap-2 bg-[#29BC9B] hover:bg-[#22a989] text-[#050505] text-[12px] tracking-[0.22em] uppercase font-bold px-5 py-3.5 rounded-md transition-colors"
                >
                  <Download size={14} />
                  Instalar ahora
                </button>
              ) : (
                <div className="flex-1 text-[12px] text-white/60 leading-relaxed">
                  Abre el menú de Chrome <span className="text-white/85">⋮</span> y elige{" "}
                  <span className="text-white/90 font-medium">&quot;Instalar app&quot;</span>.
                </div>
              )}
              <button
                onClick={handleDismiss}
                data-testid="install-pwa-android-later"
                className="text-[11px] tracking-[0.22em] uppercase text-white/45 hover:text-white/80 transition-colors px-2 py-2"
              >
                Más tarde
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Keyframes scoped to this component */}
      <style>{`
        @keyframes pwaRise {
          0% { opacity: 0; transform: translate(-50%, 18px); }
          100% { opacity: 1; transform: translate(-50%, 0); }
        }
      `}</style>
    </div>
  );
}
